/**
 * Video Script PDF Generator
 *
 * Generates professionally formatted PDFs for video scripts with
 * metadata, script content, and production notes.
 *
 * PDF Specifications:
 * - US Letter (8.5" x 11" / 612 x 792 points)
 * - Margins: 60pt left/right, 72pt top/bottom
 * - Font: Helvetica family throughout
 * - Title Page with metadata
 * - Script content with proper paragraph formatting
 * - Production notes section
 * - Page numbers and copyright on every page
 */

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import {
  loadUnicodeFontFamily,
  detectLanguage,
  requiresUnicodeFont,
} from './pdfFonts';
import type { PDFFont } from './pdfFonts';
import {
  normalizeContent,
  sanitizeForPdf,
  wrapText,
  safeDrawText,
} from './pdfTextUtils';

// ============================================================================
// Types
// ============================================================================

export interface VideoScriptPdfTranslationOverrides {
  /** Translated document title */
  title?: string;
  /** Translated date string (e.g., "15 de enero de 2024") */
  date?: string;
  /** Firm/organization name (typically kept as-is) */
  organizationName?: string;
  /** Translated full copyright notice */
  copyrightNotice?: string;
  /** Translated short copyright for footers */
  copyrightShort?: string;
  /** Translated page number format (e.g., "Pagina {current}") */
  pageNumberFormat?: string;
}

export interface BuildVideoScriptPdfParams {
  /** Title of the video script */
  title: string;
  /** Estimated video length (e.g., "2 minutes, 30 seconds") */
  estimatedLength: string;
  /** Full script content */
  script: string;
  /** Production notes for the script */
  productionNotes: {
    hookStrategy: string;
    recommendedTone: string;
    emphasisPoints: string[];
    suggestedPacing: string;
  };
  /** Character count of the script */
  characterCount: number;
  /** Word count of the script */
  wordCount: number;
  /** Date of script creation */
  createdAt?: Date;
  /** Optional translation overrides for creating translated PDFs */
  translations?: VideoScriptPdfTranslationOverrides;
}

// ============================================================================
// Constants - PDF Specifications
// ============================================================================

// Page dimensions (US Letter)
const PAGE_WIDTH = 612; // 8.5 inches in points
const PAGE_HEIGHT = 792; // 11 inches in points

// Margins
const MARGIN_LEFT = 60;
const MARGIN_RIGHT = 60;
const MARGIN_TOP = 72;
const MARGIN_BOTTOM = 72;

// Calculated content area
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;

// Font sizes
const FONT_SIZE_TITLE = 18;
const FONT_SIZE_SUBTITLE = 12;
const FONT_SIZE_SECTION_HEADER = 14;
const FONT_SIZE_BODY = 10;
const FONT_SIZE_LABEL = 10;
const FONT_SIZE_METADATA = 9;
const FONT_SIZE_FOOTER = 8;
const FONT_SIZE_PAGE_NUM = 10;

// Line heights and spacing
const LINE_HEIGHT_BODY = 16; // 1.6x line height for readability
const LINE_HEIGHT_SCRIPT = 18; // Slightly more spacing for script readability
const PARAGRAPH_SPACING = 14;
const SECTION_SPACING = 24;
const BULLET_SPACING = 8;

// Indents
const BULLET_INDENT = 24;
const LABEL_VALUE_GAP = 6;

// Footer position
const FOOTER_Y = 36;
const PAGE_NUM_Y = 48;

// Organization info
const ORGANIZATION = 'The Law Offices of Fernando Hidalgo, Inc.';

// ============================================================================
// Helper: Check if production notes have content
// ============================================================================

function hasProductionNotes(notes: BuildVideoScriptPdfParams['productionNotes']): boolean {
  return !!(
    notes.hookStrategy ||
    notes.recommendedTone ||
    notes.emphasisPoints.length > 0 ||
    notes.suggestedPacing
  );
}

// ============================================================================
// PDF Builder
// ============================================================================

export async function buildVideoScriptPdf(params: BuildVideoScriptPdfParams): Promise<File> {
  const createdAt = params.createdAt || new Date();
  const year = createdAt.getFullYear();
  const translations = params.translations || {};

  // Use translated values if provided, otherwise use defaults
  const organizationName = translations.organizationName || ORGANIZATION;
  const dateStr =
    translations.date ||
    createdAt.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  const copyrightNotice =
    translations.copyrightNotice ||
    `(c) ${year} ${organizationName}. Generated by Prest0n. Facilitated by ${organizationName}. All rights reserved.`;
  const copyrightShort =
    translations.copyrightShort || `(c) ${year} ${organizationName}. All rights reserved.`;
  const pageNumberFormat = translations.pageNumberFormat || 'Page {current}';

  const pdfDoc = await PDFDocument.create();

  // Detect if content requires Unicode fonts (for Korean/CJK characters)
  const contentToCheck = [
    params.script,
    params.title,
    params.estimatedLength,
    params.productionNotes?.hookStrategy,
    params.productionNotes?.recommendedTone,
    params.productionNotes?.suggestedPacing,
    ...(params.productionNotes?.emphasisPoints || []),
    translations.title,
    translations.date,
    translations.copyrightNotice,
    translations.copyrightShort,
    translations.pageNumberFormat,
  ].filter(Boolean).join(' ');

  const needsUnicode = requiresUnicodeFont(contentToCheck);
  const languageHint = detectLanguage(contentToCheck);

  // Load appropriate fonts based on content language
  let helvetica: PDFFont;
  let helveticaBold: PDFFont;
  let helveticaOblique: PDFFont;

  if (needsUnicode) {
    // Load Unicode fonts (Noto Sans KR) for CJK content
    const fonts = await loadUnicodeFontFamily(pdfDoc, languageHint);
    helvetica = fonts.regular;
    helveticaBold = fonts.bold;
    helveticaOblique = fonts.italic; // CJK fonts typically don't have italic, using regular
  } else {
    // Load standard Helvetica fonts for Latin-only content (smaller file size)
    helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
    helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    helveticaOblique = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
  }

  // Set PDF metadata
  const displayTitle = translations.title || params.title || 'Video Script';
  const sanitizedTitle = sanitizeForPdf(displayTitle, needsUnicode);
  pdfDoc.setTitle(sanitizedTitle);
  pdfDoc.setAuthor(organizationName);
  pdfDoc.setSubject('Video Script');
  pdfDoc.setKeywords(['video script', 'legal', 'immigration']);
  pdfDoc.setProducer(organizationName);
  pdfDoc.setCreator('prest0');
  pdfDoc.setCreationDate(createdAt);
  pdfDoc.setModificationDate(createdAt);

  const allPages: ReturnType<typeof pdfDoc.addPage>[] = [];
  let lastContentPageIndex = 0; // Track last page that received content

  // State for current page and cursor
  let page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  allPages.push(page);
  let cursorY = PAGE_HEIGHT - MARGIN_TOP;

  // Helper functions
  const addNewPage = (): void => {
    page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    allPages.push(page);
    cursorY = PAGE_HEIGHT - MARGIN_TOP;
  };

  // Reserve space for footer
  const CONTENT_BOTTOM_LIMIT = PAGE_NUM_Y + 30;

  const ensureSpace = (needed: number): void => {
    if (cursorY - needed < CONTENT_BOTTOM_LIMIT) {
      addNewPage();
    }
  };

  const drawText = (
    text: string,
    x: number,
    y: number,
    font: typeof helvetica,
    size: number,
    color = rgb(0, 0, 0)
  ): void => {
    safeDrawText(page, text, { x, y, size, font, color });
  };

  const drawWrappedText = (
    text: string,
    font: typeof helvetica,
    size: number,
    indent: number,
    lineHeight: number,
    extraSpacing: number,
    color = rgb(0, 0, 0)
  ): void => {
    const maxWidth = CONTENT_WIDTH - indent;
    const wrapped = wrapText(text, font, size, maxWidth);
    for (const line of wrapped) {
      ensureSpace(lineHeight);
      drawText(line, MARGIN_LEFT + indent, cursorY, font, size, color);
      cursorY -= lineHeight;
      lastContentPageIndex = allPages.length - 1; // Track that this page has content
    }
    cursorY -= extraSpacing;
  };

  // ============================================================================
  // TITLE PAGE
  // ============================================================================
  cursorY = PAGE_HEIGHT - MARGIN_TOP - 100;

  // Document title
  const titleLines = wrapText(sanitizedTitle, helveticaBold, FONT_SIZE_TITLE, CONTENT_WIDTH);
  for (const line of titleLines) {
    const titleWidth = helveticaBold.widthOfTextAtSize(line, FONT_SIZE_TITLE);
    drawText(line, (PAGE_WIDTH - titleWidth) / 2, cursorY, helveticaBold, FONT_SIZE_TITLE);
    cursorY -= 24;
  }

  cursorY -= 8;

  // Subtitle: "Video Script"
  const subtitleText = 'Video Script';
  const subtitleWidth = helveticaOblique.widthOfTextAtSize(subtitleText, FONT_SIZE_SUBTITLE);
  drawText(
    subtitleText,
    (PAGE_WIDTH - subtitleWidth) / 2,
    cursorY,
    helveticaOblique,
    FONT_SIZE_SUBTITLE,
    rgb(0.4, 0.4, 0.4)
  );
  cursorY -= 28;

  // Metadata line: "Est. Length: X:XX | Words: XXXX | Characters: XXXXX"
  const metadataText = `Est. Length: ${sanitizeForPdf(params.estimatedLength, needsUnicode)} | Words: ${params.wordCount.toLocaleString()} | Characters: ${params.characterCount.toLocaleString()}`;
  const metadataWidth = helvetica.widthOfTextAtSize(metadataText, FONT_SIZE_METADATA);
  drawText(
    metadataText,
    (PAGE_WIDTH - metadataWidth) / 2,
    cursorY,
    helvetica,
    FONT_SIZE_METADATA,
    rgb(0.5, 0.5, 0.5)
  );
  cursorY -= 40;

  // Date
  const dateWidth = helvetica.widthOfTextAtSize(dateStr, FONT_SIZE_BODY);
  drawText(dateStr, (PAGE_WIDTH - dateWidth) / 2, cursorY, helvetica, FONT_SIZE_BODY, rgb(0.4, 0.4, 0.4));
  cursorY -= 60;

  // Copyright notice on title page
  const copyrightLines = wrapText(copyrightNotice, helveticaOblique, FONT_SIZE_FOOTER, CONTENT_WIDTH - 40);
  for (const line of copyrightLines) {
    const lineWidth = helveticaOblique.widthOfTextAtSize(line, FONT_SIZE_FOOTER);
    drawText(
      line,
      (PAGE_WIDTH - lineWidth) / 2,
      cursorY,
      helveticaOblique,
      FONT_SIZE_FOOTER,
      rgb(0.5, 0.5, 0.5)
    );
    cursorY -= 12;
  }
  lastContentPageIndex = 0; // Title page has content

  // ============================================================================
  // SCRIPT CONTENT PAGE(S)
  // ============================================================================
  addNewPage();

  // Section header: "SCRIPT"
  drawText('SCRIPT', MARGIN_LEFT, cursorY, helveticaBold, FONT_SIZE_SECTION_HEADER);
  cursorY -= SECTION_SPACING;

  // Normalize script content (strip thinking blocks, sanitize encoding, merge paragraphs)
  const sanitizedScript = normalizeContent(params.script, needsUnicode);

  // Split script into paragraphs and render
  const paragraphs = sanitizedScript.split(/\n\n+/);
  for (const para of paragraphs) {
    const trimmedPara = para.trim();
    if (!trimmedPara) continue;
    drawWrappedText(trimmedPara, helvetica, FONT_SIZE_BODY, 0, LINE_HEIGHT_SCRIPT, PARAGRAPH_SPACING);
  }

  // ============================================================================
  // PRODUCTION NOTES PAGE (if present)
  // ============================================================================
  if (hasProductionNotes(params.productionNotes)) {
    addNewPage();

    // Section header: "PRODUCTION NOTES"
    drawText('PRODUCTION NOTES', MARGIN_LEFT, cursorY, helveticaBold, FONT_SIZE_SECTION_HEADER);
    cursorY -= SECTION_SPACING;

    const { hookStrategy, recommendedTone, emphasisPoints, suggestedPacing } = params.productionNotes;

    // Hook Strategy
    if (hookStrategy) {
      drawText('Hook Strategy:', MARGIN_LEFT, cursorY, helveticaBold, FONT_SIZE_LABEL);
      cursorY -= LINE_HEIGHT_BODY + LABEL_VALUE_GAP;
      drawWrappedText(
        sanitizeForPdf(hookStrategy, needsUnicode),
        helvetica,
        FONT_SIZE_BODY,
        0,
        LINE_HEIGHT_BODY,
        PARAGRAPH_SPACING
      );
    }

    // Recommended Tone
    if (recommendedTone) {
      drawText('Recommended Tone:', MARGIN_LEFT, cursorY, helveticaBold, FONT_SIZE_LABEL);
      cursorY -= LINE_HEIGHT_BODY + LABEL_VALUE_GAP;
      drawWrappedText(
        sanitizeForPdf(recommendedTone, needsUnicode),
        helvetica,
        FONT_SIZE_BODY,
        0,
        LINE_HEIGHT_BODY,
        PARAGRAPH_SPACING
      );
    }

    // Emphasis Points
    if (emphasisPoints.length > 0) {
      drawText('Emphasis Points:', MARGIN_LEFT, cursorY, helveticaBold, FONT_SIZE_LABEL);
      cursorY -= LINE_HEIGHT_BODY + LABEL_VALUE_GAP;
      for (const point of emphasisPoints) {
        const sanitizedPoint = sanitizeForPdf(point, needsUnicode);
        drawWrappedText(`*  ${sanitizedPoint}`, helvetica, FONT_SIZE_BODY, BULLET_INDENT, LINE_HEIGHT_BODY, BULLET_SPACING);
      }
      cursorY -= PARAGRAPH_SPACING - BULLET_SPACING;
    }

    // Suggested Pacing
    if (suggestedPacing) {
      drawText('Suggested Pacing:', MARGIN_LEFT, cursorY, helveticaBold, FONT_SIZE_LABEL);
      cursorY -= LINE_HEIGHT_BODY + LABEL_VALUE_GAP;
      drawWrappedText(
        sanitizeForPdf(suggestedPacing, needsUnicode),
        helvetica,
        FONT_SIZE_BODY,
        0,
        LINE_HEIGHT_BODY,
        PARAGRAPH_SPACING
      );
    }
  }

  // ============================================================================
  // REMOVE TRAILING EMPTY PAGES
  // ============================================================================
  // Pages created by ensureSpace() but never written to should be removed
  while (allPages.length - 1 > lastContentPageIndex && allPages.length > 1) {
    pdfDoc.removePage(allPages.length - 1);
    allPages.pop();
  }

  // ============================================================================
  // ADD PAGE NUMBERS AND FOOTERS TO ALL PAGES
  // ============================================================================
  for (let idx = 0; idx < allPages.length; idx++) {
    const pg = allPages[idx];
    const pageNum = idx + 1;

    // Page number (bottom center)
    const pageNumText = pageNumberFormat.replace('{current}', String(pageNum));
    const pageNumWidth = helvetica.widthOfTextAtSize(pageNumText, FONT_SIZE_PAGE_NUM);
    safeDrawText(pg, pageNumText, {
      x: (PAGE_WIDTH - pageNumWidth) / 2,
      y: PAGE_NUM_Y,
      size: FONT_SIZE_PAGE_NUM,
      font: helvetica,
      color: rgb(0.4, 0.4, 0.4),
    });

    // Footer copyright (on every page)
    const footerWidth = helvetica.widthOfTextAtSize(copyrightShort, FONT_SIZE_FOOTER);
    safeDrawText(pg, copyrightShort, {
      x: (PAGE_WIDTH - footerWidth) / 2,
      y: FOOTER_Y,
      size: FONT_SIZE_FOOTER,
      font: helvetica,
      color: rgb(0.5, 0.5, 0.5),
    });
  }

  // ============================================================================
  // SAVE AND RETURN
  // ============================================================================
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });

  // Generate filename
  const slugify = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);
  };
  const subjectSlug = slugify(params.title || 'video-script');
  const dateSlug = createdAt.toISOString().slice(0, 10);
  const filename = `${subjectSlug}-video-script-${dateSlug}.pdf`;

  return new File([blob], filename, { type: 'application/pdf', lastModified: createdAt.getTime() });
}
